"use client";

import React, { createContext, useContext, useMemo, ReactNode } from "react";

/**
 * 오답노트 복습 가리기 컨텍스트 (JCG-2060).
 *
 * `/my/error-notes` 같은 "복습" 화면에서 정답·풀이·오답노트의 가림(모자이크) 상태를 ProblemAnswer
 * 트리에 내려주는 opt-in 신호. Provider 가 없는 페이지(이론/코딩/시험/퀴즈 등)에서는
 * `useErrorNoteReview()` 가 null 을 반환해 기존 동작을 100% 유지한다 — 다른 페이지에 영향 없음.
 *
 * - `mosaicHidden`: 전역 가리기 토글 상태. true 면 정답(가운데)·풀이/오답노트(우측 셸 패널)를
 *   무채색 픽셀 모자이크로 덮는다(영역 클릭 시 해당 영역만 드러남). 기본 false(=보이기).
 *   풀이/오답노트는 다른 셸 페이지와 동일하게 우측 사이드바로 열린다(셸 솔루션 모드 유지).
 * - `activeProblemId`: 스크롤 중(화면 중앙)인 문제 id. page 의 scroll-spy 가 설정한다. 각
 *   ProblemAnswer 는 자기 id 와 비교해 활성이면 우측 풀이/오답노트를 자동으로 연다(클릭 불필요).
 *
 * 상태는 page(ErrorNotesPage)가 소유하고 value 로 내려주는 controlled provider 다.
 */
export interface ErrorNoteReviewContextValue {
  mosaicHidden: boolean;
  activeProblemId: string | null;
}

const ErrorNoteReviewContext = createContext<ErrorNoteReviewContextValue | null>(
  null,
);

export const useErrorNoteReview = (): ErrorNoteReviewContextValue | null =>
  useContext(ErrorNoteReviewContext);

interface ErrorNoteReviewProviderProps extends ErrorNoteReviewContextValue {
  children: ReactNode;
}

export const ErrorNoteReviewProvider: React.FC<ErrorNoteReviewProviderProps> = ({
  mosaicHidden,
  activeProblemId,
  children,
}) => {
  const value = useMemo(
    () => ({ mosaicHidden, activeProblemId }),
    [mosaicHidden, activeProblemId],
  );
  return (
    <ErrorNoteReviewContext.Provider value={value}>
      {children}
    </ErrorNoteReviewContext.Provider>
  );
};
